import { Suspense } from "react";
import type { HeadersFunction } from "react-router";
import { Await, data, useLoaderData } from "react-router";

import { SWR } from "~/.server/caching.server";
import { getShopperContext } from "~/.server/shopper-context.server";
import { getTimingContext } from "~/.server/timing.server";
import { getClient } from "~/contentful/contentful.server";
import { deferDataRequest } from "~/lib/utils-server/http.server";
import { getPageMeta } from "~/seo/servers/seo.server";
import { productSearch } from "~/shop/products/productSearch.server";

import { useContentfulLiveUpdates } from "@contentful/live-preview/react";

import type { TypeGenericTemplateSkeleton } from "~/contentful/compiled/TypeGenericTemplate";
import { GENERIC_TEMPLATE } from "~/contentful/contents";
import { componentsSwitch } from "~/lib/generic-template-helper";

import { Skeletons } from "../product-list/components/skeletons";

const BREADCRUMBS_CATEGORIES = [{ name: "Blog", id: "pages" }];

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const loaderHeadersSimple = [];
  loaderHeaders.forEach(lHeader => loaderHeadersSimple.push(lHeader));

  return {
    "Cache-Control": "s-maxage=60, stale-while-revalidate",
    ...loaderHeaders,
  };
};

export const loader = async ({
  params,
  context: { waitUntil, locale },
  request,
}: LoaderArgs) => {
  const { time } = getTimingContext();
  // tidy the inputs
  const slug = params.slug;
  if (!slug) throw new Response("Not found", { status: 404 });
  if (Array.isArray(slug)) throw new Response("Not found", { status: 404 });

  const url = new URL(request.url);
  const preview = !!url.searchParams.get("preview");
  const client = getClient(preview);

  /* Caching template */
  const [swr, recomendationSwr] = SWR.stable(
    "post",
    ["contentful", "genericTemplate", "article", slug],
    undefined,
    !preview,
  );
  const sourceCode = getShopperContext()?.sourceCode;

  const recomendationData = recomendationSwr(
    () =>
      productSearch({
        refine: ["cgid=root"],
        limit: 10,
      }),
    [sourceCode],
  );

  const templateData = await swr(
    async () => {
      return await client.getEntries<TypeGenericTemplateSkeleton>({
        content_type: GENERIC_TEMPLATE,
        "fields.slug": slug,
        limit: 1,
        include: 10,
      });
    },
    [slug],
    /* tags */
    data => {
      return [data.items[0].sys.id];
    },
  );

  const templateContent = templateData.items[0];
  const seoData = templateData.items[0].fields.seo;

  const [seo] = await time(
    deferDataRequest(
      Promise.resolve(seoData).then(seoData =>
        getPageMeta({
          type: "article",
          options: {
            url: request.url,
            h1: seoData?.fields.h1,
            title: seoData?.fields.title,
            description: seoData?.fields.description,
            lang: locale.alias,
          },
          robots: {
            noFollow: seoData?.fields.followLinks === "nofollow" ? true : false,
            noIndex:
              seoData?.fields.indexedBySearchEngines === "noindex"
                ? true
                : false,
          },
          og: {
            type: seoData?.fields.ogtype,
            title: seoData?.fields.ogtitle,
            description: seoData?.fields.ogdescription,
            url: seoData?.fields.ogurl,
            image: seoData?.fields.ogimage,
          },
        }),
      ),
    ),
    {
      type: "seo",
      desc: "SEO",
    },
  );

  return data(
    {
      templateContent,
      seo,
      recomendationData,
    },
    {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
      },
    },
  );
};

export default function IndexPage() {
  const { templateContent: _templateContent } = useLoaderData<typeof loader>();
  const templateContent = useContentfulLiveUpdates(_templateContent);

  return (
    <section className="">
      <Suspense fallback={<Skeletons />}>
        <Await resolve={templateContent}>
          {templateContent => (
            <div className="flex flex-col gap-10 md:gap-12 lg:gap-16">
              {templateContent.fields.components.map((content, index) => {
                return (
                  content &&
                  componentsSwitch(
                    content.sys.contentType.sys.id,
                    content,
                    index,
                  )
                );
              })}
            </div>
          )}
        </Await>
      </Suspense>
    </section>
  );
}
